import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { FileUploadZone } from './upload/FileUploadZone';
import { ValidationErrors } from './upload/ValidationErrors';
import { WarningMessage } from './upload/WarningMessage';
import { useGuidesStore } from '../../stores/guidesStore';
import { Download, Copy, Check } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const BulkLinkModal = ({ isOpen, onClose }: Props) => {
  const [file, setFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [copiedGuide, setCopiedGuide] = useState<string | null>(null);
  const [availableGuides, setAvailableGuides] = useState<Array<{id: string, name: string}>>([]);
  const { translations } = useLanguage();
  const { guides } = useGuidesStore();
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  const navigate = useNavigate();

  // Initialize available guides based on user role
  useEffect(() => {
    const userRole = localStorage.getItem('userRole');
    const userId = localStorage.getItem('userId');

    if (userRole === 'organizer' && userId) {
      // Filter guides for this organizer
      const organizerGuides = Object.values(guides).filter(g => g.organizerId === userId);
      setAvailableGuides(organizerGuides.map(g => ({ id: g.id, name: g.name })));
    } else {
      // For admin, show all guides
      setAvailableGuides(Object.values(guides).map(g => ({ id: g.id, name: g.name })));
    }
  }, [guides]);

  const handleDownloadTemplate = () => {
    const template = [
      {
        identifier: '123456789',
        identifier_type: 'passport', // Only passport is supported
        guide_name: userRole === 'guide' ? '' : 'Guide Full Name',
        guide_passport: '', // Optional: guide passport number
        is_owner: 'true' // New field for ownership status
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'bulk-link-template.xlsx');
  };

  const handleCopyGuideName = async (guideName: string) => {
    try {
      await navigator.clipboard.writeText(guideName);
      setCopiedGuide(guideName);
      setTimeout(() => setCopiedGuide(null), 2000);
      toast.success(translations?.bulkLink?.copySuccess || 'Guide name copied');
    } catch (error) {
      toast.error(translations?.bulkLink?.copyError || 'Failed to copy');
    }
  };

  const handleUpload = () => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      if (data) {
        // Parse Excel file to check for email entries
        const workbook = XLSX.read(data, { type: 'binary' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(worksheet);

        // Check for any email entries
        const hasEmailEntries = rows.some((row: any) => 
          row.identifier_type?.toLowerCase() === 'email'
        );

        if (hasEmailEntries) {
          toast.error(translations?.bulkLink?.emailSearchSuspended || 'Email search is temporarily suspended. Please use passport numbers only.');
          return;
        }

        // Close modal first
        onClose();
        
        // Navigate to status page with file data
        navigate('/bulk-link-status', {
          state: { fileData: data },
          replace: true
        });
      }
    };
    reader.readAsBinaryString(file);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title={translations?.bulkLink?.title || 'Bulk Link Pilgrims'}
    >
      <div className="space-y-6">
        {/* Email Search Suspended Warning */}
        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-yellow-800">
              {translations?.bulkLink?.emailSearchSuspended || 'Email search is temporarily suspended'}
            </span>
          </div>
          <p className="mt-2 text-sm text-yellow-600">
            {translations?.bulkLink?.usePassportOnly || 'Please use passport numbers only for bulk linking.'}
          </p>
        </div>

        {/* Available Guides - Only show for organizer */}
        {userRole === 'organizer' && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-3">
              {translations?.bulkLink?.availableGuides || 'Available Guides'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {availableGuides.map((guide) => (
                <motion.div
                  key={guide.id}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white p-3 rounded-lg shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{guide.name}</span>
                    <button
                      onClick={() => handleCopyGuideName(guide.name)}
                      className="p-1.5 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                    >
                      {copiedGuide === guide.name ? (
                        <Check size={16} className="text-green-500" />
                      ) : (
                        <Copy size={16} />
                      )}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Template Download */}
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
        >
          <Download size={20} />
          <span>{translations?.bulkLink?.downloadTemplate || 'Download Excel Template'}</span>
        </button>

        {/* File Upload */}
        <FileUploadZone
          file={file}
          onFileSelect={setFile}
          onFileRemove={() => {
            setFile(null);
            setValidationErrors([]);
          }}
        />

        <ValidationErrors errors={validationErrors} />
        <WarningMessage />

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.bulkLink?.cancel || 'Cancel'}
          </button>
          <button
            onClick={handleUpload}
            disabled={!file}
            className={`px-6 py-2 rounded-lg text-white
              ${!file
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            {translations?.bulkLink?.upload || 'Upload'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
